const TOKEN_KEY = 'casemania.token'

export function getToken() {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token)
    else localStorage.removeItem(TOKEN_KEY)
  } catch {
    // Приватный режим — работаем без запоминания сессии.
  }
}

export class ApiError extends Error {
  constructor(message, status, code) {
    super(message)
    this.status = status
    this.code = code
  }
}

async function request(method, path, body, options = {}) {
  const headers = {}
  if (body !== undefined) headers['Content-Type'] = 'application/json'

  const token = getToken()
  if (token && options.auth !== false) headers.Authorization = `Bearer ${token}`

  let response
  try {
    response = await fetch(`/api${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    })
  } catch {
    throw new ApiError('Нет связи с сервером. Проверь, запущен ли бэкенд.', 0)
  }

  if (response.status === 204) return null

  let payload = null
  try {
    payload = await response.json()
  } catch {
    if (!response.ok) {
      throw new ApiError('Сервер временно недоступен', response.status)
    }
  }

  if (!response.ok) {
    throw new ApiError(payload?.error ?? 'Сервер временно недоступен', response.status, payload?.code)
  }

  return payload
}

const query = (params) => {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && value !== '') search.set(key, String(value))
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}

export const api = {
  // Серверная диагностика и проверка связи
  health: () => request('GET', '/health', undefined, { auth: false }),

  // Авторизация
  register: (data) => request('POST', '/auth/register', data, { auth: false }),
  login: (data) => request('POST', '/auth/login', data, { auth: false }),
  me: () => request('GET', '/auth/me'),

  // Справочник и витрина
  catalog: () => request('GET', '/catalog', undefined, { auth: false }),
  cases: () => request('GET', '/cases', undefined, { auth: false }),
  caseById: (id) => request('GET', `/cases/${id}`, undefined, { auth: false }),
  openCase: (id, count) => request('POST', `/cases/${id}/open`, { count }),
  liveDrops: () => request('GET', '/drops/live', undefined, { auth: false }),

  // Крафты
  craftMeta: () => request('GET', '/crafts/meta', undefined, { auth: false }),
  craftPreview: (inventoryIds) => request('POST', '/crafts/preview', { inventoryIds }),
  craftExecute: (inventoryIds) => request('POST', '/crafts/execute', { inventoryIds }),

  // Апгрейд
  upgradeTargets: (params) => request('GET', `/upgrade/targets${query(params)}`),
  upgradeExecute: (data) => request('POST', '/upgrade/execute', data),

  // Инвентарь
  inventory: () => request('GET', '/inventory'),
  inventoryHistory: () => request('GET', '/inventory/history'),
  sell: (inventoryIds) => request('POST', '/inventory/sell', { inventoryIds }),
  sellAll: () => request('POST', '/inventory/sell-all'),
  withdraw: (inventoryId, tradeUrl) => request('POST', '/inventory/withdraw', { inventoryId, tradeUrl }),
  withdrawals: () => request('GET', '/inventory/withdrawals'),
  getTradeUrl: () => request('GET', '/inventory/trade-url'),
  saveTradeUrl: (tradeUrl) => request('POST', '/inventory/trade-url', { tradeUrl }),

  // Market.CSGO
  marketStatus: () => request('GET', '/market/status', undefined, { auth: false }),

  // Платежи
  paymentMethods: () => request('GET', '/payments/methods', undefined, { auth: false }),
  createPayment: (data) => request('POST', '/payments/create', data),
  payment: (id) => request('GET', `/payments/${id}`),
  cancelPayment: (id) => request('POST', `/payments/${id}/cancel`),
  payments: () => request('GET', '/payments'),
  redeemPromo: (code) => request('POST', '/payments/promo', { code }),

  // Честность и статистика
  fairState: () => request('GET', '/fair/state'),
  fairRotate: (clientSeed) => request('POST', '/fair/rotate', { clientSeed }),
  fairRolls: () => request('GET', '/fair/rolls'),
  fairVerify: (serverSeed) => request('POST', '/fair/verify', { serverSeed }, { auth: false }),
  stats: () => request('GET', '/stats/me'),
}
